/* eslint-disable react-hooks/exhaustive-deps */
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { validate } from "validate.js";
import {
  addApplication,
  checkPendingApplication,
  resetStateApplications,
  setFormValues,
  updateApplication,
} from "../../app/slices/applicationsSlice";
import { resetStateAttachments } from "../../app/slices/attachmentsSlice";
import { showSnackbar } from "../../app/slices/commonSlice";
import { resetStateRisks } from "../../app/slices/risksSlice";
import {
  addSolution,
  resetStateSolutions,
  updateSolution,
} from "../../app/slices/solutionsSlice";
import { resetStateStakeholders } from "../../app/slices/stakeholdersSlice";
import { fetchUsers, resetStateUsers } from "../../app/slices/usersSlice";
import { isEmpty, oracleDateFormat } from "../../app/utils";
import TopLinks from "../../components/TopLinks/TopLinks";
import ErrorContainer from "../../components/errorContainer/errorContainer";
import LoadingContainer from "../../components/loadingContainer/loadingContainer";
import "./Applications.css";
import AttachmentsFragment from "./Fragments/Attachments.Frag";
import CompanyDetailsFragment from "./Fragments/CompanyDetails.Frag";
import DirectorsFragment from "./Fragments/Directors.Frag";
import FinalizationFragment from "./Fragments/Finalization.Frag";
import RisksFragmentFragment from "./Fragments/RisksFragment.Frag";
import ShareHoldersFragment from "./Fragments/ShareHolders.Frag";
import SolutionDetailsFragment from "./Fragments/SolutionDetails.Frag";
import SolutionSummaryFragment from "./Fragments/SolutionSummary.Frag";
import {
  companyDetailsSchema,
  solutionDetailsSchema,
  solutionSummarySchema,
} from "./validationSchemas";
import CommentsContainer from "../../components/CommentsContainer/CommentsContainer";
import moment from "moment";

const ApplicationForm = () => {
  const { formValues } = useSelector(({ applications }) => applications);
  const { activeSession } = useSelector(({ auth }) => auth);
  const {
    data: dataPostApplication,
    error: errorPostApplication,
    status: statusPostApplication,
  } = useSelector(({ applications }) => applications.post);
  const {
    data: dataPutApplication,
    error: errorPutApplication,
    status: statusPutApplication,
  } = useSelector(({ applications }) => applications.put);
  const {
    data: dataPostAttachments,
    error: errorPostAttachments,
    status: statusPostAttachments,
  } = useSelector(({ attachments }) => attachments.post);
  const {
    data: dataPutAttachments,
    error: errorPutAttachments,
    status: statusPutAttachments,
  } = useSelector(({ attachments }) => attachments.put);
  const {
    data: dataPostRisk,
    error: errorPostRisk,
    status: statusPostRisks,
  } = useSelector(({ risks }) => risks.post);
  const {
    data: dataPutRisk,
    error: errorPutRisk,
    status: statusPutRisk,
  } = useSelector(({ risks }) => risks.put);
  const {
    data: dataPostStakeholders,
    error: errorPostStakeholders,
    status: statusPostStakeholders,
  } = useSelector(({ stakeholders }) => stakeholders.post);
  const {
    data: dataPutStakeholders,
    error: errorPutStakeholders,
    status: statusPutStakeholders,
  } = useSelector(({ stakeholders }) => stakeholders.put);
  const {
    data: dataPostSolution,
    error: errorPostSolution,
    status: statusPostSolution,
  } = useSelector(({ solutions }) => solutions.post);
  const {
    data: dataPutSolution,
    error: errorPutSolution,
    status: statusPutSolution,
  } = useSelector(({ solutions }) => solutions.put);
  const {
    data: pendingData,
    error: errorPending,
    status: statusPending,
  } = useSelector(({ applications }) => applications.checkPending);
  const { status: statusUsers, list: listUsers } = useSelector(
    ({ users }) => users.get
  );

  const [errors, setErrors] = useState([]);
  const [allFormErrors, setAllFormErrors] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [isAttended, setIsAttended] = useState(false);
  const [hasPendingApplication, setHasPendingApplication] = useState(null);
  const [openComments, setOpenComments] = useState(false);

  const topLinks = [
    {
      name: "Dashboard",
      path: "/dashboard",
    },
    {
      name: "Apply",
      isCurrent: false,
    },
  ];

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const steps = [
    "Company Details",
    "Shareholders",
    "Directors",
    "Solution Summary",
    "Solution Details",
    "Risks",
    "Attachments",
    "Finalization",
  ];

  //use effects
  useEffect(() => {
    dispatch(resetStateUsers(["get"]));
    resetStatesPostPut();
    dispatch(
      setFormValues({
        ...formValues,
        testingPeriod: formValues?.testingPeriod || "12",
      })
    );

    if (isEmpty(formValues?.applicationId)) {
      //set default company details from login user
      dispatch(
        fetchUsers({
          USER_ID: activeSession?.USER_ID,
        })
      );

      //check pending applications by user
      dispatch(
        checkPendingApplication({
          INCORPORATION_NUMBER: activeSession?.INCORPORATION_NUMBER,
        })
      );
    } else {
      setHasPendingApplication(false);
      if (formValues?.status === "R") {
        setOpenComments(true);
      }
    }
    return () => {
      dispatch(resetStateApplications());
      dispatch(resetStateUsers());
      dispatch(resetStateAttachments());
      dispatch(resetStateSolutions());
      dispatch(resetStateStakeholders());
    };
  }, []);

  useEffect(() => {
    if (listUsers?.length > 0) {
      const _user = listUsers[0];
      dispatch(
        setFormValues({
          ...formValues,
          companyName: _user?.COMPANY_NAME,
          incorporationNumber: _user?.INCORPORATION_NUMBER,
          contactEmail: _user?.EMAIL,
          contactPhone: _user?.PHONE_NUMBER,
          contactPerson: _user?.FULL_NAME,
        })
      );
    }
  }, [listUsers]);

  useEffect(() => {
    if (dataPostApplication) {
      dispatch(
        setFormValues({
          ...formValues,
          applicationId: dataPostApplication,
        })
      );
    }
    if (dataPostSolution) {
      dispatch(
        setFormValues({
          ...formValues,
          solutionId: dataPostSolution,
        })
      );
    }
    if (
      dataPutApplication ||
      dataPostApplication ||
      dataPostStakeholders ||
      dataPutStakeholders ||
      dataPostAttachments ||
      dataPutAttachments ||
      dataPostRisk ||
      dataPutRisk ||
      dataPostSolution ||
      dataPutSolution
    ) {
      dispatch(
        showSnackbar({
          open: true,
          message:
            activeStep !== 7
              ? "Changes saved successfully"
              : "Application submitted successfully.",
          type: "success",
        })
      );
      if (activeStep === 7) {
        setIsAttended(true);
      }
      resetStatesPostPut();
    }
  }, [
    dataPostApplication,
    dataPutApplication,
    dataPostStakeholders,
    dataPutStakeholders,
    dataPostAttachments,
    dataPutAttachments,
    dataPostRisk,
    dataPutRisk,
    dataPostSolution,
    dataPutSolution,
  ]);

  useEffect(() => {
    if (
      errorPostApplication ||
      errorPutApplication ||
      errorPostStakeholders ||
      errorPutStakeholders ||
      errorPostAttachments ||
      errorPutAttachments ||
      errorPostRisk ||
      errorPutRisk ||
      errorPostSolution ||
      errorPutSolution
    ) {
      dispatch(
        showSnackbar({
          open: true,
          message:
            errorPostApplication?.message ||
            errorPutApplication?.message ||
            errorPostStakeholders?.message ||
            errorPutStakeholders?.message ||
            errorPostAttachments?.message ||
            errorPutAttachments?.message ||
            errorPostRisk?.message ||
            errorPutRisk?.message ||
            errorPostSolution?.message ||
            errorPutSolution?.message ||
            "Something went wrong",
          type: "error",
          timeout: null,
        })
      );
    }
  }, [
    errorPostApplication,
    errorPutApplication,
    errorPostStakeholders,
    errorPutStakeholders,
    errorPostAttachments,
    errorPutAttachments,
    errorPostRisk,
    errorPutRisk,
    errorPostSolution,
    errorPutSolution,
  ]);

  useEffect(() => {
    if (pendingData !== null) {
      setHasPendingApplication(!pendingData);
    }
  }, [pendingData]);

  //methods
  const handleStepChange = (_step) => {
    if (_step > 0 && isEmpty(formValues?.applicationId)) {
      dispatch(
        showSnackbar({
          open: true,
          message: "Complete step 1 (Company Details) before proceeding",
          type: "warning",
          timeout: 3000,
        })
      );
      return;
    } else if (_step === 4 && isEmpty(formValues?.applicationId)) {
      dispatch(
        showSnackbar({
          open: true,
          message:
            "Complete step 4 (Solution Summary) before proceeding to step 5",
          type: "warning",
          timeout: 3000,
        })
      );
      return;
    }

    setActiveStep(_step);
  };

  const handleSave = () => {
    if (validateForm()) {
      switch (steps[activeStep]?.toLowerCase()) {
        case "company details":
          if (isEmpty(formValues?.applicationId)) {
            //create new application
            dispatch(
              addApplication({
                COMPANY_NAME: formValues?.companyName,
                // CATEGORY: formValues,
                PHYSICAL_ADDRESS: formValues?.physicalAddress,
                POSTAL_ADDRESS: formValues?.postalAddress,
                CONTACT_PERSON_EMAIL: formValues?.contactEmail,
                CONTACT_PERSON_NAME: formValues?.contactPerson,
                CONTACT_PERSON_PHONE: formValues?.contactPhone,
                WEBSITE: formValues?.website || null,
                INCORPORATION_NUMBER: formValues?.incorporationNumber,
              })
            );
          } else {
            //update application
            dispatch(
              updateApplication({
                id: formValues?.applicationId,
                payload: {
                  COMPANY_NAME: formValues?.companyName,
                  PHYSICAL_ADDRESS: formValues?.physicalAddress,
                  POSTAL_ADDRESS: formValues?.postalAddress,
                  CONTACT_PERSON_EMAIL: formValues?.contactEmail,
                  CONTACT_PERSON_NAME: formValues?.contactPerson,
                  WEBSITE: formValues?.website || null,
                  INCORPORATION_NUMBER: formValues?.incorporationNumber,
                },
              })
            );
          }
          break;
        case "shareholders":
        case "directors":
        case "risks":
        case "attachments":
          dispatch(
            showSnackbar({
              open: true,
              message: "Changes saved successfully",
              type: "success",
            })
          );
          break;
        case "solution summary":
          if (isEmpty(formValues?.solutionId)) {
            dispatch(
              addSolution({
                APPLICATION_ID: formValues?.applicationId,
                SOLUTION_NAME: formValues?.solutionName,
                NATURE_OF_OPERATION: formValues?.natureOfOperation,
                SCALE: formValues?.scaleOfOperation,
                MARKET: JSON.stringify(formValues?.marketOfOperation),
                ATTRIBUTE: formValues?.solutionAttribute,
                TESTING_PERIOD: formValues?.testingPeriod,
                TIME_TO_DEPLOY: formValues?.deploymentDuration,
                TECHNOLOGY_USED: JSON.stringify(formValues?.technologiesUsed),
                SOURCE_OF_FUND: JSON.stringify(formValues?.sourceOfFunds),
                OBJECTIVE: JSON.stringify(formValues?.solutionObjectives),
              })
            );
          } else {
            dispatch(
              updateSolution({
                id: formValues?.solutionId,
                payload: {
                  SOLUTION_NAME: formValues?.solutionName,
                  NATURE_OF_OPERATION: formValues?.natureOfOperation,
                  SCALE: formValues?.scaleOfOperation,
                  MARKET: JSON.stringify(formValues?.marketOfOperation),
                  ATTRIBUTE: formValues?.solutionAttribute,
                  TESTING_PERIOD: formValues?.testingPeriod,
                  TIME_TO_DEPLOY: formValues?.deploymentDuration,
                  TECHNOLOGY_USED: JSON.stringify(formValues?.technologiesUsed),
                  SOURCE_OF_FUND: JSON.stringify(formValues?.sourceOfFunds),
                  OBJECTIVE: JSON.stringify(formValues?.solutionObjectives),
                },
              })
            );
          }
          break;
        case "solution details":
          dispatch(
            updateSolution({
              id: formValues?.solutionId,
              payload: {
                PROBLEM_DESCRIPTION: formValues?.problemDescription,
                SOLUTION_DESCRIPTION: formValues?.solutionDescription,
                LEGAL_CHALLENGES: formValues?.legalChallenges,
                REGULATORY_EXEMPTIONS: formValues?.specialExemptions,
              },
            })
          );
          break;
        default:
          break;
      }
    } else {
    }
  };

  const handleSubmit = () => {
    if (formValues?.hasAgreed || false) {
      if (validateAllErrors()) {
        dispatch(
          updateApplication({
            id: formValues?.applicationId,
            payload: {
              STATUS: "S",
              SUBMITTED_AT: moment().format(oracleDateFormat),
            },
          })
        );
      } else {
        dispatch(
          showSnackbar({
            open: true,
            message: "Please resolve the errors shown above before proceeding",
            timeout: 6000,
            type: "error",
          })
        );
      }
    } else {
      dispatch(
        showSnackbar({
          open: true,
          message: "Read the information and check the box above to continue",
          type: "warning",
        })
      );
    }
  };

  const validateForm = () => {
    let _errors;
    let e;
    switch (steps[activeStep]?.toLowerCase()) {
      case "company details":
        _errors = validate(formValues, companyDetailsSchema);
        break;
      case "shareholders":
        //validate shareholders
        e = [];
        if ((formValues?.shareholders || [])?.length === 0) {
          e.push("No shareholders added");
        }
        if (e?.length > 0) {
          _errors = { shareholders: e };
        }
        break;
      case "directors":
        //validate directors
        e = [];
        if ((formValues?.directors || [])?.length === 0) {
          e.push("No directors added");
        }
        if (e?.length > 0) {
          _errors = { directors: e };
        }
        break;
      case "solution summary":
        _errors = validate(formValues, solutionSummarySchema);
        break;
      case "solution details":
        _errors = validate(formValues, solutionDetailsSchema);
        break;
      case "risks":
        e = [];
        if ((formValues?.risks || [])?.length === 0) {
          e.push("No risks added");
        }
        if (e?.length > 0) {
          _errors = { risks: e };
        }
        break;
      case "attachments":
        e = validateAttachments();
        if (e?.attachments?.length > 0) {
          _errors = e;
        }
        break;
      default:
        break;
    }

    if (_errors) {
      setErrors(_errors);
      return false;
    } else {
      setErrors([]);
      return true;
    }
  };

  const validateAllErrors = () => {
    let _errors = [];

    //helper
    const _pushErrors = (e, step) => {
      if (e) {
        for (let key in e) {
          e[`${key}`].forEach((err) => {
            _errors.push(`${err} - (Step ${step})`);
          });
        }
      }
    };
    _pushErrors(validate(formValues, companyDetailsSchema), 1);
    _pushErrors(
      (() => {
        //validate shareholders
        let e = [];
        if ((formValues?.shareholders || [])?.length === 0) {
          e.push("No shareholders added");
        }
        return { shareholders: e };
      })(),
      2
    );
    _pushErrors(
      (() => {
        //validate directors
        let e = [];
        if ((formValues?.directors || [])?.length === 0) {
          e.push("No directors added");
        }
        return { directors: e };
      })(),
      3
    );
    _pushErrors(validate(formValues, solutionSummarySchema), 4);
    _pushErrors(validate(formValues, solutionDetailsSchema), 5);
    _pushErrors(
      (() => {
        //validate risks
        let e = [];
        if ((formValues?.risks || [])?.length === 0) {
          e.push("No risks added");
        }
        return { risks: e };
      })(),
      6
    );
    _pushErrors(validateAttachments(), 7);

    if (_errors?.length === 0) {
      setAllFormErrors(null);
      return true;
    } else {
      setAllFormErrors(_errors);
      return false;
    }
  };

  const validateAttachments = () => {
    let _errors = [];
    if (
      formValues?.attachments?.find((x) => x?.TYPE === "Application letter") ===
      undefined
    ) {
      _errors.push("Application letter not attached");
    }
    if (
      formValues?.attachments?.find((x) => x?.TYPE === "Business plan") ===
      undefined
    ) {
      _errors.push("Business plan not attached");
    }
    if (
      formValues?.attachments?.find((x) => x?.TYPE === "Testing plan") ===
      undefined
    ) {
      _errors.push("Testing plan not attached");
    }
    if (
      formValues?.attachments?.find((x) => x?.TYPE === "Exit strategy") ===
      undefined
    ) {
      _errors.push("Exit strategy not attached");
    }
    if (
      formValues?.attachments?.find(
        (x) => x?.TYPE === "Certificate of incorporation"
      ) === undefined
    ) {
      _errors.push("Certificate of incorporation not attached");
    }
    if (
      formValues?.attachments?.find((x) => x?.TYPE === "Risk profile") ===
      undefined
    ) {
      _errors.push("Risk profile not attached");
    }
    if (
      formValues?.attachments?.find(
        (x) => x?.TYPE === "Agreement between applicant and owner"
      ) === undefined
    ) {
      _errors.push("Agreement between applicant and owner not attached");
    }
    if (
      formValues?.attachments?.find(
        (x) => x?.TYPE === "Memorandum and articles of association"
      ) === undefined
    ) {
      _errors.push("Memorandum and articles of association no attached");
    }
    if (
      formValues?.attachments?.find(
        (x) => x?.TYPE === "Original letter from the registrar of companies"
      ) === undefined
    ) {
      _errors.push(
        "Original letter from the registrar of companies not attached"
      );
    }
    if (
      formValues?.attachments?.find(
        (x) => x?.TYPE === "Shareholders' declaration"
      ) === undefined
    ) {
      _errors.push("Shareholders' declaration not attached");
    }

    return { attachments: _errors };
  };

  const isBusy = () => {
    return (
      statusUsers === "loading" ||
      statusPostApplication === "loading" ||
      statusPutApplication === "loading" ||
      statusPostStakeholders === "loading" ||
      statusPutStakeholders === "loading" ||
      statusPostAttachments === "loading" ||
      statusPutAttachments === "loading" ||
      statusPostRisks === "loading" ||
      statusPutRisk === "loading" ||
      statusPostSolution === "loading" ||
      statusPutSolution === "loading"
    );
  };

  const resetStatesPostPut = () => {
    dispatch(resetStateApplications(["post", "put"]));
    dispatch(resetStateAttachments(["post", "put"]));
    dispatch(resetStateSolutions(["post", "put"]));
    dispatch(resetStateStakeholders(["post", "put"]));
    dispatch(resetStateRisks(["post", "put"]));
  };

  //builders
  const BuildSteps = () => {
    return (
      <div className="app-stepper">
        {formValues?.status === "R" && (
          <div
            className="step"
            onClick={() => {
              setOpenComments(true);
            }}
          >
            <div className="step-marker">Comments</div>
          </div>
        )}
        {steps.map((step, index) => (
          <div
            className="step"
            onClick={() => {
              handleStepChange(index);
            }}
          >
            <div
              className={`step-marker ${
                activeStep === index ? "active-marker" : ""
              }`}
            >
              {step}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const BuildStepView = () => {
    return (
      <div className="app-step-view">
        {/* {JSON.stringify(formValues)} */}
        {(() => {
          switch (activeStep) {
            case 0:
              return <CompanyDetailsFragment errors={errors} />;
            case 1:
              return <ShareHoldersFragment errors={errors} />;
            case 2:
              return <DirectorsFragment errors={errors} />;
            case 3:
              return <SolutionSummaryFragment errors={errors} />;
            case 4:
              return <SolutionDetailsFragment errors={errors} />;
            case 5:
              return <RisksFragmentFragment errors={errors} />;
            case 6:
              return <AttachmentsFragment errors={errors} />;
            case 7:
              return <FinalizationFragment allFormErrors={allFormErrors} />;
            default:
              return <>Unknown step</>;
          }
        })()}
        {/* Action buttons */}
        {BuildActionButtons()}
      </div>
    );
  };

  const BuildActionButtons = () => {
    return (
      <div className="app-padding mt-20 mb-30">
        <div className="flex-row flex-wrap align-items-center justify-content-end action-button-container">
          {!isAttended && (
            <>
              {activeStep > 0 && (
                <Button
                  className="btn-login flex-row"
                  style={{ margin: "3px 5px" }}
                  variant="contained"
                  onClick={() => {
                    handleStepChange(activeStep - 1);
                  }}
                  margin="dense"
                  size="medium"
                  color="primary"
                  disabled={isBusy()}
                >
                  Previous
                </Button>
              )}
              <Button
                style={{ margin: "3px 5px" }}
                className="btn-login flex-row"
                variant="contained"
                onClick={
                  activeStep === steps?.length - 1 ? handleSubmit : handleSave
                }
                margin="dense"
                size="medium"
                color="secondary"
                disabled={isBusy()}
              >
                {isBusy() && (
                  <CircularProgress
                    size={20}
                    color="primary"
                    style={{ marginRight: "8px" }}
                  />
                )}
                {activeStep === steps?.length - 1 ? "Submit" : "Save Changes"}
              </Button>
              {activeStep < steps?.length - 1 && (
                <Button
                  className="btn-login flex-row"
                  style={{ margin: "3px 5px" }}
                  variant="contained"
                  onClick={() => {
                    handleStepChange(activeStep + 1);
                  }}
                  margin="dense"
                  size="medium"
                  color="primary"
                  disabled={isBusy()}
                >
                  Next
                </Button>
              )}
            </>
          )}
          {isAttended && (
            <Button
              style={{ margin: "3px 5px" }}
              className="flex-row"
              variant="contained"
              onClick={() => {
                navigate("/dashboard");
              }}
              margin="dense"
              size="medium"
              color="secondary"
              disabled={isBusy()}
            >
              Finish
            </Button>
          )}
        </div>
      </div>
    );
  };

  const BuildPendingView = () => {
    return (
      <>
        <div
          style={{ minHeight: "300px" }}
          className="flex-column align-items-center justify-content-center"
        >
          <h2 className="mb-30">Oops,</h2>
          <div style={{ width: "450px" }} className="text-align-center">
            Looks like you have a draft or ongoing application. Go to "My
            Applications" from the dashboard to view your saved or submitted
            applications.
          </div>
          <Button
            style={{ margin: "30px 5px" }}
            variant="contained"
            onClick={() => {
              navigate("/");
            }}
            margin="dense"
            size="medium"
            color="primary"
          >
            GO BACK
          </Button>
        </div>
      </>
    );
  };

  return (
    <>
      <TopLinks links={topLinks} />
      <div className="application-form-container">
        {/* {JSON.stringify(formValues)} */}
        {hasPendingApplication === false && (
          <>
            {BuildSteps()}
            {BuildStepView()}
          </>
        )}
        <div style={{ width: "100%" }}>
          {statusPending === "loading" && (
            <LoadingContainer message="Loading..." />
          )}
          {errorPending && statusPending !== "loading" && (
            <ErrorContainer
              message={errorPending?.message || "Something went wrong"}
              onReload={() => {
                //check pending applications by user
                dispatch(
                  checkPendingApplication({
                    INCORPORATION_NUMBER: activeSession?.INCORPORATION_NUMBER,
                  })
                );
              }}
            />
          )}
          {hasPendingApplication === true && statusPending === "idle" && (
            <>{BuildPendingView()}</>
          )}
        </div>
      </div>

      {/* dialog view comments */}
      {openComments && (
        <Dialog open={openComments} maxWidth={"sm"} fullWidth>
          <DialogTitle id="responsive-dialog-title">Comments</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <CommentsContainer
                formValues={formValues}
                recordId={formValues?.applicationId}
                showForm={false}
                setFormValues={setFormValues}
              />
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              id="btn-close"
              margin="dense"
              color="secondary"
              variant="contained"
              onClick={() => {
                setOpenComments(false);
              }}
              style={{ marginLeft: "10px" }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {/* dialog view comments */}
    </>
  );
};

export default ApplicationForm;
